import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { ConfirmDialog } from '../../security/components/confirm-dialog';

const LABEL_MAX_LEN = 64;

export interface CreateKeyDialogProps {
  open: boolean;
  submitting: boolean;
  inlineError: string | null;
  onSubmit: (label: string | null) => void;
  onCancel: () => void;
}

export function CreateKeyDialog({
  open,
  submitting,
  inlineError,
  onSubmit,
  onCancel,
}: CreateKeyDialogProps) {
  const { t } = useTranslation('credentials');
  const formId = useId();
  const labelId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [label, setLabel] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLabel('');
      setValidationError(null);
      queueMicrotask(() => inputRef.current?.focus());
    }
  }, [open]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = label.trim();
    if (trimmed.length > LABEL_MAX_LEN) {
      setValidationError('create_label_too_long');
      return;
    }
    setValidationError(null);
    onSubmit(trimmed === '' ? null : trimmed);
  }

  return (
    <ConfirmDialog
      open={open}
      title={t('create_title')}
      subtitle={t('create_subtitle')}
      submitLabel={t('create_submit')}
      cancelLabel={t('create_cancel')}
      submitting={submitting}
      formId={formId}
      onCancel={onCancel}
    >
      <form id={formId} onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.5rem' }}>
        <FieldRow>
          <Label htmlFor={labelId}>{t('create_label_field')}</Label>
          <Input
            ref={inputRef}
            id={labelId}
            type="text"
            autoComplete="off"
            maxLength={LABEL_MAX_LEN}
            placeholder={t('create_label_placeholder')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            data-testid="create-key-label"
          />
          <FieldError
            message={
              validationError ? t(validationError) : (inlineError ?? undefined)
            }
          />
        </FieldRow>
      </form>
    </ConfirmDialog>
  );
}
